/**
 * Proxy API endpoints — typed wrappers for the ServisSearch CRUDL routes.
 * Shapes mirror the upstream API (see servissearch-api/docs/openapi.yaml).
 * Only the fields the UI actually uses are typed; everything else passes
 * through opaquely to avoid coupling to upstream internals.
 */

import { api } from "./api-client";

// ── Tenants (upstream "business entity") ────────────────────────────────────
export interface Tenant {
  id: string;
  numericId?: number;
  name: string;
  identifier?: string;
  status?: string;
  knowledge_base_id?: string | null;
  knowledge_base_status?: string | null;
  data_source_id?: string | null;
  documents_bucket?: string | null;
  vector_bucket_name?: string | null;
  vector_index_name?: string | null;
  vector_index_arn?: string | null;
  embedding_model_arn?: string | null;
  llm_provider?: LlmProvider | null;
  effective_defaults?: EffectiveDefaults | null;
  custom_metadata?: Record<string, unknown> | null;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
  hasTenantAdminKey?: boolean;
  hasTenantReaderKey?: boolean;
  hasTenantWriterKey?: boolean;
}

export interface LlmProvider {
  type?: "bedrock" | "openai-compatible" | string;
  openai_config?: {
    base_url?: string;
    api_key?: string;
    default_model?: string;
    organization?: string;
    extra_params?: Record<string, unknown>;
  };
  default_model?: string;
  default_system_prompt?: string;
  default_temperature?: number;
  default_max_tokens?: number;
  extra_params?: Record<string, unknown>;
}

export interface EffectiveDefaults {
  llm_provider_type?: "bedrock" | "openai-compatible" | string;
  llm_model?: string;
  llm_temperature?: number;
  llm_max_tokens?: number;
  embedding_model?: string;
}

export const tenantApi = {
  list: () => api.get<{ business: Tenant[] }>("/api/tenants"),
  create: (body: { name: string; description?: string }) =>
    api.post<Tenant>("/api/tenants", body),
  get: (id: string) => api.get<Tenant>(`/api/tenants/${id}`),
  update: (id: string, body: Partial<Omit<Pick<Tenant, "name" | "description" | "llm_provider">, "llm_provider">> & { llm_provider?: LlmProvider | null }) =>
    api.patch<Tenant>(`/api/tenants/${id}`, body),
  storeAdminKey: (id: string, adminKey: string) =>
    api.put<{ ok: true }>(`/api/tenants/${id}/admin-key`, { adminKey }),
  storeRoleKey: (id: string, role: "reader" | "writer", key: string) =>
    api.put<{ ok: true }>(`/api/tenants/${id}/role-key`, { role, key }),
  delete: (id: string, confirm = false, expectedName?: string) => {
    const q = new URLSearchParams();
    if (confirm) q.set("confirm", "true");
    if (expectedName) q.set("expectedName", expectedName);
    const qs = q.toString();
    return api.del<{ ok: true }>(`/api/tenants/${id}${qs ? `?${qs}` : ""}`);
  },
};

// ── API Keys ────────────────────────────────────────────────────────────────
export type KeyRole = "ADMIN" | "WRITER" | "READER";
export interface ApiKey {
  id: string;
  name: string;
  role: KeyRole;
  is_active: boolean;
  collection_ids: string[];
  expires_at?: string | null;
  last_used_at?: number | null;
  created_at?: string;
}
export interface CreatedApiKey extends ApiKey {
  /** Plaintext key value — returned ONLY at creation. Never re-shown. */
  key?: string;
  /** Older/alternate upstream shape. */
  api_key?: string;
}
export type RevokeApiKeyInput = Pick<ApiKey, "name" | "role" | "collection_ids" | "created_at">;

export const apiKeyApi = {
  list: (tenantId: string) =>
    api.get<{ keys: ApiKey[] }>(`/api/tenants/${tenantId}/keys`),
  create: (tenantId: string, body: { name: string; role: KeyRole; collection_ids?: string[]; expires_at?: string }) =>
    api.post<CreatedApiKey>(`/api/tenants/${tenantId}/keys`, body),
  revoke: (tenantId: string, keyId: string, expected: RevokeApiKeyInput) =>
    api.del<{ ok: true }>(`/api/tenants/${tenantId}/keys/${keyId}`, { expected }),
};

// ── Collections ─────────────────────────────────────────────────────────────
export interface Collection {
  id: string;
  name: string;
  description?: string | null;
  collection_type?: string | null;
  parent_collection_id?: string | null;
  is_active: boolean;
  chunking_config?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  has_data_source?: boolean;
  data_source_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CollectionDocumentCount {
  total: number;
  active: number;
  archived: number;
  statuses: Record<string, number>;
  truncated: boolean;
}

export interface CollectionDocumentCountsResponse {
  counts: Record<string, CollectionDocumentCount>;
  exact_up_to: number;
  source: "documents-list";
}

export const collectionApi = {
  list: (params?: { tenantId?: string }) =>
    api.get<{ collections: Collection[] }>(withTenant("/api/collections", params?.tenantId)),
  documentCounts: (params?: { tenantId?: string; collectionIds?: string[] }) => {
    const q = new URLSearchParams();
    if (params?.tenantId) q.set("tenantId", params.tenantId);
    for (const collectionId of params?.collectionIds ?? []) {
      q.append("collection_id", collectionId);
    }
    const qs = q.toString();
    return api.get<CollectionDocumentCountsResponse>(`/api/collections/document-counts${qs ? `?${qs}` : ""}`);
  },
  create: (body: Partial<Collection> & { name: string }, params?: { tenantId?: string }) =>
    api.post<Collection>(withTenant("/api/collections", params?.tenantId), body),
  get: (id: string, params?: { tenantId?: string }) =>
    api.get<Collection>(withTenant(`/api/collections/${id}`, params?.tenantId)),
  update: (id: string, body: Partial<Collection>, params?: { tenantId?: string }) =>
    api.patch<Collection>(withTenant(`/api/collections/${id}`, params?.tenantId), body),
  delete: (id: string, confirm = false, params?: { tenantId?: string }) =>
    api.del<{ ok: true }>(withTenant(`/api/collections/${id}${confirm ? "?confirm=true" : ""}`, params?.tenantId)),
  startIngestion: (id: string, params?: { tenantId?: string }) =>
    api.post<unknown>(withTenant(`/api/collections/${id}/ingestion/start`, params?.tenantId), {}),
  listJobs: (id: string, params?: { tenantId?: string }) =>
    api.get<{ jobs: unknown[] }>(withTenant(`/api/collections/${id}/ingestion/jobs`, params?.tenantId)),
  recreateDatasource: (id: string, params?: { tenantId?: string }) =>
    api.post<unknown>(withTenant(`/api/collections/${id}/datasource/recreate`, params?.tenantId), {}),
};

// ── Documents ───────────────────────────────────────────────────────────────
export interface Document {
  id: string;
  filename: string;
  file_type: string;
  collection_id: string;
  shared_collection_ids?: string[];
  metadata?: Record<string, unknown>;
  processing_status: string;
  is_archived: boolean;
  created_at?: string;
}

export type DocumentPreviewKind = "text" | "markdown" | "html";

export interface DocumentPreview {
  filename: string;
  kind: DocumentPreviewKind;
  content: string;
  content_length: number | null;
  truncated: boolean;
}

export interface DocumentStatus {
  processing_status: string;
  error_details?: string;
  archive_status?: {
    is_archived: boolean;
    archived_at?: string;
  };
}
export interface DocumentAnalytics {
  total_documents: number;
  active_documents: number;
  archived_documents: number;
  by_status: Record<string, number>;
  by_file_type: Record<string, number>;
  by_collection: Record<string, number>;
}
export interface AnalyticsOverview {
  documents?: { total?: number };
  collections?: { total?: number };
  searches?: { last_7_days?: number };
  knowledge_base?: { id?: string | null; status?: string };
}
export interface SearchAnalytics {
  popular_queries?: Array<{ query: string; count: number }>;
  performance_metrics?: {
    avg_execution_time_ms?: number;
    avg_results_count?: number;
    avg_similarity_score?: number;
  };
}
export interface UsageAnalytics {
  period_days: number;
  total_search_calls: number;
  total_feedback_submissions: number;
  daily_search_calls: Record<string, number>;
  avg_daily_searches: number;
}
export interface ProxyLatencyAnalytics {
  count: number;
  p50: number;
  p95: number;
}
export interface PresignResponse {
  document_id: string | number;
  upload_url: string;
  expires_at?: number;
}
export interface UploadDocumentResponse {
  document_id: string | number;
  sync?: unknown;
  sync_error?: string;
}

export const documentApi = {
  list: (params?: { tenantId?: string; collection_id?: string; limit?: number; show_archived?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.tenantId) q.set("tenantId", params.tenantId);
    if (params?.collection_id) q.set("collection_id", params.collection_id);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.show_archived) q.set("show_archived", "true");
    const qs = q.toString();
    return api.get<{ documents: Document[] }>(`/api/documents${qs ? `?${qs}` : ""}`);
  },
  requestUpload: (body: { filename: string; collection_id: string; content_type?: string }, params?: { tenantId?: string }) =>
    api.post<PresignResponse>(withTenant("/api/documents", params?.tenantId), body),
  upload: (body: {
    file: File;
    filename: string;
    collection_id: string;
    content_type: string;
  }, params?: { tenantId?: string; autoSync?: boolean }) => {
    const q = new URLSearchParams({
      filename: body.filename,
      collection_id: body.collection_id,
      content_type: body.content_type,
    });
    if (params?.tenantId) q.set("tenantId", params.tenantId);
    if (params?.autoSync === false) q.set("auto_sync", "false");
    return api.upload<UploadDocumentResponse>(`/api/documents/upload?${q.toString()}`, body.file, {
      "Content-Type": "application/octet-stream",
    });
  },
  get: (id: string, params?: { tenantId?: string }) =>
    api.get<Document>(withTenant(`/api/documents/${id}`, params?.tenantId)),
  status: (id: string, params?: { tenantId?: string }) =>
    api.get<DocumentStatus>(withTenant(`/api/documents/${id}/status`, params?.tenantId)),
  preview: (id: string, params?: { tenantId?: string }) =>
    api.get<DocumentPreview>(withTenant(`/api/documents/${id}/preview`, params?.tenantId)),
  update: (id: string, body: Partial<Pick<Document, "is_archived">> & { shared_collection_ids?: string[]; metadata?: Record<string, unknown> }, params?: { tenantId?: string }) =>
    api.patch<Document>(withTenant(`/api/documents/${id}`, params?.tenantId), body),
  delete: (id: string, params?: { tenantId?: string }) =>
    api.del<{ ok: true }>(withTenant(`/api/documents/${id}`, params?.tenantId)),
  bulkDelete: (documentIds: string[]) =>
    api.del<{ ok: true }>("/api/documents/bulk", { document_ids: documentIds }),
  sync: (id: string, params?: { tenantId?: string }) =>
    api.post<unknown>(withTenant(`/api/documents/${id}/sync`, params?.tenantId), {}),
};

export const analyticsApi = {
  overview: (params?: { tenantId?: string }) =>
    api.get<AnalyticsOverview>(withTenant("/api/analytics", params?.tenantId)),
  search: (params?: { tenantId?: string; days?: number }) => {
    const path = withTenant("/api/analytics/search", params?.tenantId);
    const separator = path.includes("?") ? "&" : "?";
    return api.get<SearchAnalytics>(`${path}${separator}days=${params?.days ?? 7}`);
  },
  documents: (params?: { tenantId?: string }) =>
    api.get<DocumentAnalytics>(withTenant("/api/analytics/documents", params?.tenantId)),
  usage: (params?: { tenantId?: string; days?: number }) => {
    const path = withTenant("/api/analytics/usage", params?.tenantId);
    const separator = path.includes("?") ? "&" : "?";
    return api.get<UsageAnalytics>(`${path}${separator}days=${params?.days ?? 30}`);
  },
  proxyLatency: () =>
    api.get<ProxyLatencyAnalytics>("/api/analytics/proxy-latency"),
};

export const knowledgeBaseApi = {
  verify: (tenantId: string) => api.get<unknown>(`/api/knowledge-bases/${encodeURIComponent(tenantId)}/verify`),
};

function withTenant(path: string, tenantId?: string): string {
  if (!tenantId) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}tenantId=${encodeURIComponent(tenantId)}`;
}
